import { FC } from 'react';

import checkIcon from 'assets/images/checkIcon.svg';

import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';
import { fontFamily, fontSize, lineHeight } from 'atoms/typography/typography';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Image, { ImageData } from 'quarks/Image';
import Text from 'quarks/Text';

import richTextParser from 'utils/richTextParser';

import type {
  ContentfulEntityPerson,
  ContentfulTemplateBlogBody,
  ContentfulTemplateCaseStudyBody,
  ContentfulTemplateEguidesBody,
  ContentfulTemplatePodcastBody,
  ContentfulTemplateResourceBody,
  ContentfulTemplateTipsAndToolsBody,
  Maybe,
} from 'graphqlTypes';

const scrollPosition = 'scroll-margin-top: 150px;';

const bodyCSS = `
  word-wrap: break-word;
  p {
    color: ${color.gray[900]};
  }
  h1, h2, h3, h4, h5, h6 {
    color: ${color.gray[900]};
    font-family: ${fontFamily.secondaryFont};
    
    span {
      font-family: ${fontFamily.secondaryFont};
    }
  }
  h2 {
    font-size: ${fontSize.h3};
    line-height: ${lineHeight.h3};
  }
  h3 {
    font-size: ${fontSize.h4};
    line-height: ${lineHeight.h4};
  }
  h4 {
    font-size: ${fontSize.textLg};
    line-height: ${lineHeight.textLg};
  }
  h5, h6 {
    font-size: ${fontSize.textMd};
    line-height: ${lineHeight.textMd};
  }
  ul {
    li {
      padding-left: 30px;
      &:before {
        background-image: url(${checkIcon});
        background-color: ${color.common.transparent};
        background-position: center;
        background-repeat: no-repeat;
        background-size: 100%;
        width: 20px;
        height: 20px;
        content: '';
        position: absolute;
        top: 4px;
        left: 0;
      }
    }
  }
`;

type BodyProps = {
  body?: Maybe<
    | ContentfulTemplateTipsAndToolsBody
    | ContentfulTemplateResourceBody
    | ContentfulTemplatePodcastBody
    | ContentfulTemplateEguidesBody
    | ContentfulTemplateCaseStudyBody
    | ContentfulTemplateBlogBody
  >;
  author?: Maybe<ContentfulEntityPerson>;
  type?: string;
};

const Body: FC<BodyProps> = ({ body, author, type }) => (
  <Flex
    flexDirection="column"
    width="100%"
    maxWidth="100%"
    gap="24px"
    lg={{ maxWidth: '770px' }}
    className="resource-body"
  >
    {type === 'ContentfulTemplatePodcast' && (
      <Flex fontSize="h5" lineHeight="h5" textColor="gray-900" fontWeight="bold">
        Episode Overview
      </Flex>
    )}
    {body && (
      <Flex flexDirection="column" gap="32px" width="100%" css={bodyCSS}>
        {richTextParser(body)}
      </Flex>
    )}
    {author?.headshot && author?.position && author?.name && (
      <Flex
        id="author-bio"
        border="1px solid"
        borderColor="gray-200"
        borderRadius="16px"
        backgroundColor="gray-50"
        paddingAll={24}
        css={scrollPosition}
        md={{ paddingAll: 40 }}
      >
        <Flex gap="24px" flexDirection="column" sm={{ flexDirection: 'row' }}>
          <Container
            maxWidth="100px"
            minWidth="100px"
            height="100px"
            borderRadius="50%"
            overflowX="hidden"
            overflowY="hidden"
          >
            <Image
              image={author.headshot as ImageData}
              width="100%"
              height="100%"
              alt={author?.name || 'Author Name'}
            />
          </Container>
          <Flex flexDirection="column">
            <Text textStyle="textMd" fontWeight="semiBold" textColor="gray-900">
              {author.name}
            </Text>
            <Text textStyle="textXs" textColor="green-600" fontWeight="semiBold">
              {author.position}
            </Text>
            <Flex gap="4px" marginTop={16}>
              <Flex paddingAll={8} backgroundColor="green-300" borderRadius="8px" cursor="pointer">
                <Icon id="linkedin-round" fill={color.common.white} size={16} />
              </Flex>
              <Flex paddingAll={8} backgroundColor="green-300" borderRadius="8px" cursor="pointer">
                <Icon id="twitter" fill={color.common.white} size={16} />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    )}
  </Flex>
);
export default Body;
